import { FinWordContent, QuizContent } from "./types";

import wordsData from "@/data/daily/words.json";
import quizzesData from "@/data/daily/quizzes.json";

/**
 * Calculates the absolute day index since "2026-01-01".
 */
export function getAbsoluteDayIndex(dateStr: string): number {
  const parts = dateStr.split("-");
  if (parts.length !== 3) return 0;
  
  const year = Number(parts[0]);
  const month = Number(parts[1]) - 1;
  const day = Number(parts[2]);
  
  const baseDate = new Date(Date.UTC(2026, 0, 1));
  const currentDate = new Date(Date.UTC(year, month, day));
  const diffTime = currentDate.getTime() - baseDate.getTime();
  const oneDay = 1000 * 60 * 60 * 24;
  
  const dayIndex = Math.floor(diffTime / oneDay);
  return Math.max(0, dayIndex);
}

export function getWordForDate(dateStr: string): FinWordContent {
  const index = getAbsoluteDayIndex(dateStr);
  // Ensure we safely access the array index
  const words = wordsData as unknown as FinWordContent[];
  return words[index % words.length];
}

export function getQuizForDate(dateStr: string, countryCode: string = "us"): QuizContent {
  const index = getAbsoluteDayIndex(dateStr);
  const allQuizzes = quizzesData as unknown as QuizContent[];
  
  // 1. Gather all questions from quizzesData
  const rawQuestions: any[] = [];
  for (const quiz of allQuizzes) {
    rawQuestions.push(...quiz.questions);
  }

  // 2. Filter out questions that are US-only if the user is in another country
  const normalizedCountry = countryCode.toLowerCase();
  const countryFiltered = rawQuestions.filter((q) => {
    if (normalizedCountry !== "us" && q.countrySpecific === "us") {
      return false; // Skip US-specific question for other countries
    }
    return true;
  });

  // 3. Deduplicate questions by their normalized question text to avoid repetitions
  const uniqueMap = new Map<string, any>();
  for (const q of countryFiltered) {
    const key = q.question.trim().toLowerCase();
    if (!uniqueMap.has(key)) {
      uniqueMap.set(key, q);
    }
  }
  const uniqueQuestions = Array.from(uniqueMap.values());

  // 4. Deterministically select 4 questions for this date
  const selectedQuestions: any[] = [];
  const poolSize = uniqueQuestions.length;

  for (let i = 0; i < 4; i++) {
    // Determine the question index in the pool
    const qIndex = (index * 4 + i) % poolSize;
    selectedQuestions.push({
      ...uniqueQuestions[qIndex],
      id: `q_${index}_${i}` // stable ID for this day
    });
  }

  // 5. Build and return the day's Quiz
  return {
    id: index + 1,
    date: dateStr,
    questions: selectedQuestions,
  };
}


